
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { StateNodeData } from '@/types/workflow-graph';

interface NodeSettingsContextValue {
    /** Currently selected node for settings */
    selectedNode: StateNodeData | null;
    /** Whether the settings drawer is open */
    isSettingsOpen: boolean;
    /** Open settings drawer for a node */
    openSettings: (nodeData: StateNodeData) => void;
    /** Close settings drawer */
    closeSettings: () => void;
    /** Whether editing is allowed */
    readOnly: boolean;
    /** Available resources for the toolbox/drawer */
    availableResources: {
        /** `name` = short capability key; `fqcn` = canonical id (app_key/name) for ASL Resource suffix */
        agents: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
        tools: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
        models: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
        policies: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
        workflows: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    };
}

const NodeSettingsContext = createContext<NodeSettingsContextValue | null>(null);

interface NodeSettingsProviderProps {
    children: ReactNode;
    readOnly?: boolean;
    agents?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    tools?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    models?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    policies?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    workflows?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    /** Callback when settings drawer opens */
    onSettingsOpen?: (nodeData: StateNodeData) => void;
    /** Callback when settings drawer closes */
    onSettingsClose?: () => void;
}

export function NodeSettingsProvider({
    children,
    readOnly = true,
    agents = [],
    tools = [],
    models = [],
    policies = [],
    workflows = [],
    onSettingsOpen,
    onSettingsClose,
}: NodeSettingsProviderProps) {
    const [selectedNode, setSelectedNode] = useState<StateNodeData | null>(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const openSettings = useCallback((nodeData: StateNodeData) => {
        setSelectedNode(nodeData);
        setIsSettingsOpen(true);
        onSettingsOpen?.(nodeData);
    }, [onSettingsOpen]);

    const closeSettings = useCallback(() => {
        setIsSettingsOpen(false);
        onSettingsClose?.();
        // Delay clearing selected node to allow animation
        setTimeout(() => setSelectedNode(null), 300);
    }, [onSettingsClose]);

    const value: NodeSettingsContextValue = {
        selectedNode,
        isSettingsOpen,
        openSettings,
        closeSettings,
        readOnly,
        availableResources: { agents, tools, models, policies, workflows },
    };

    return (
        <NodeSettingsContext.Provider value={value}>
            {children}
        </NodeSettingsContext.Provider>
    );
}

export function useNodeSettings() {
    const context = useContext(NodeSettingsContext);
    if (!context) {
        throw new Error('useNodeSettings must be used within a NodeSettingsProvider');
    }
    return context;
}

